import {
  last, find, reject, isNil,
} from 'ramda';
import { Endpoints } from '@twnel/utils-js/web';
import { localStorageFactory, cookieStorageFactory } from '@twnel/web-components';

export const logException = (error, warning = false) => {
  const errorObj = warning || error instanceof Error ? error : new Error(error);
  console[warning ? 'warn' : 'error'](errorObj);
};

export const appId = ({
  prefix,
  version = '1.0.0',
  environment,
  debug = false,
} = {}) => {
  const cleanEnvironment = last(environment.split('.')).split('/')[0];
  if (prefix) {
    const versionMatch = typeof version === 'string' && version.match(/^[0-9]+\.[0-9]+/);
    if (!versionMatch) {
      throw new Error(`Unexpected version format, expected [0-9].[0-9](.[0-9])+: ${version}`);
    }
    const cleanVersion = versionMatch[0].replace('.', '_');
    return `${debug ? 'debug_' : ''}${prefix}_${cleanEnvironment}_${cleanVersion}`;
  }
  return `${debug ? 'DEBUG ' : ''}${cleanEnvironment.slice(0, 2)} ${version}`;
};

export const isSessionCompany = ({ userAgent, apikey, token } = {}) => (
  userAgent && (apikey || token)
);

export const isTwnelOrigin = (origin, environment) => {
  let originUrl;
  try {
    originUrl = new URL(origin);
  } catch {
    return false;
  }

  const {
    LOGIN_ORIGIN,
    COMPANIES_ORIGIN,
    METRICS_ORIGIN,
    BOTS_ORIGIN,
    SETTINGS_ORIGIN,
    ALPHA_ORIGIN,
    ROUTING_ORIGIN,
    TERRA_ORIGIN,
  } = Endpoints(environment);

  const { hostname } = originUrl;
  return !!find((url) => {
    const { hostname: urlHostname } = new URL(url);
    return urlHostname === hostname;
  }, reject(isNil, [
    LOGIN_ORIGIN,
    COMPANIES_ORIGIN,
    METRICS_ORIGIN,
    BOTS_ORIGIN,
    SETTINGS_ORIGIN,
    ALPHA_ORIGIN,
    ROUTING_ORIGIN,
    TERRA_ORIGIN,
    'http://localhost',
  ]));
};

export const appOrigin = ({ inSearchParam } = {}) => {
  if (inSearchParam) {
    const urlParams = new URLSearchParams(window.location.search);
    return decodeURIComponent(urlParams.get('origin'));
  }
  return window.location.origin;
};

// Not every environment has a Terra client (beta doesn't)
export const isTerraOrigin = (environment) => {
  const { TERRA_ORIGIN } = Endpoints(environment);
  return !!TERRA_ORIGIN && new URL(TERRA_ORIGIN).origin === new URL(appOrigin()).origin;
};

export const loginOrigin = ({
  environment,
  appendAppOrigin = false,
  logout = false,
}) => {
  const { LOGIN_ORIGIN } = Endpoints(environment);
  if (!appendAppOrigin) {
    return LOGIN_ORIGIN;
  }

  const query = new URLSearchParams({ origin: appOrigin() });
  if (logout) {
    query.set('logout', 'true');
  }
  const path = isTerraOrigin(environment) ? '/terra' : '';
  return `${LOGIN_ORIGIN}${path}?${query}`;
};

export const defaultOrigin = ({ environment, landscaping = false }) => {
  // Check if a return URL is present as a query param
  const origin = appOrigin({ inSearchParam: true });
  if (isTwnelOrigin(origin, environment)) {
    return origin;
  }

  // Redirect to the terra client
  const { TERRA_ORIGIN, COMPANIES_ORIGIN } = Endpoints(environment);
  if (landscaping && TERRA_ORIGIN) {
    return TERRA_ORIGIN;
  }

  // Redirect to the companies client by default
  return COMPANIES_ORIGIN;
};

export const goToURL = (url) => {
  if (!/^https?:\/\/[^\s]{2,}$/i.test(url)) {
    throw new Error(`Tried to navigate to an invalid url: ${url}`);
  }
  window.location.replace(url);
};

// The logout flag asks the login page to drop the session stored in its origin, since the
// message posted to the hidden iframe may not be processed before this page navigates away.
export const goToLogin = (environment, { logout = false } = {}) => (
  goToURL(loginOrigin({ environment, appendAppOrigin: true, logout }))
);

export const isLogoutRequest = () => new URLSearchParams(window.location.search).has('logout');

export const isSafari = () => navigator.userAgent.indexOf('Safari') !== -1
  && navigator.userAgent.indexOf('Chrome') === -1;

export const StorageFactory = (prefix) => (
  isSafari() ? cookieStorageFactory(prefix) : localStorageFactory(prefix)
);
